import React, { useEffect, useMemo, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Check, Copy, Wrench } from "lucide-react";
import { BLE_PLUGIN_VERSION } from "../../services/bleService";

function copyFallback(text) {
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  document.body.removeChild(area);
}

export const BleDiagnosticsPanel = ({ device, handshakeState, error, events }) => {
  const [appInfo, setAppInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    CapacitorApp.getInfo().then(setAppInfo).catch(() => {});
  }, []);

  const report = useMemo(
    () => ({
      generated_at: new Date().toISOString(),
      app: appInfo
        ? `${appInfo.name} ${appInfo.version} (${appInfo.build})`
        : "non disponibile",
      platform: Capacitor.getPlatform(),
      user_agent: navigator.userAgent,
      ble_plugin: BLE_PLUGIN_VERSION,
      device: {
        id: device?.deviceId || null,
        name: device?.name || null,
        rssi: device?.rssi ?? null,
        advertised_services: device?.advertisedServices || [],
      },
      handshake_state: handshakeState,
      error: error || null,
      events,
    }),
    [appInfo, device, handshakeState, error, events]
  );

  const copyReport = async () => {
    const text = JSON.stringify(report, null, 2);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      copyFallback(text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const lastEvent = events[events.length - 1];
  const lastHexEvent = [...events].reverse().find((event) => event.hex);
  const formatEventTime = (timestamp) =>
    new Intl.DateTimeFormat("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
      hour12: false,
    }).format(new Date(timestamp));
  const diagnosis = (() => {
    const stage = lastEvent?.stage || "";
    const detail = `${lastEvent?.error || ""} ${error || ""}`.toLowerCase();
    if (stage === "connect_attempt_success")
      return "Connessione CoreBluetooth riuscita; il test sta proseguendo con il protocollo del materasso.";
    if (stage === "gatt_settle_start" || stage === "gatt_settle_done")
      return "Connessione riuscita; iOS sta stabilizzando il canale GATT prima del primo comando.";
    if (stage === "gatt_discovery_start" || stage === "gatt_discovery_done")
      return "Ricerca di FF01 e del suo service padre reale nel GATT DB del materasso.";
    if (stage === "mac_notify_subscribe_start" || stage === "mac_notify_subscribed")
      return "Canale Notify FF01 attivato; il comando MAC verrà inviato dopo la sincronizzazione iOS.";
    if (stage === "mac_write_start")
      return "Invio del comando MAC tramite Write With Response sulla characteristic FF01.";
    if (stage === "mac_write_success")
      return "Il comando MAC è stato accettato dal livello GATT; attesa della risposta firmware.";
    if (stage === "mac_write_error")
      return detail.includes("characteristic not found")
        ? "FF01 è stata richiesta sotto un service errato o non più disponibile. Il report include il GATT DB reale."
        : "Il collegamento BLE è attivo, ma iOS ha rifiutato la scrittura FF01. Il codice nativo completo è nel report.";
    if (stage === "mac_response_timeout")
      return "Scrittura riuscita, ma il firmware non ha inviato il MAC via Notify entro il timeout.";
    if (stage === "wifi_validation_result" && lastEvent?.valid === false)
      return `Lista WiFi incompleta o non valida (${lastEvent?.reason || "formato sconosciuto"}); l'app ritenterà con un buffer vuoto.`;
    if (stage === "wifi_retry_wait")
      return "Nuovo tentativo di lettura reti programmato; nessuna rete parziale verrà mostrata.";
    if (stage === "credentials_disconnected_after_write")
      return "Credenziali inviate; il dispositivo si è disconnesso come previsto e il flusso può continuare.";
    if (stage === "credentials_response_timeout")
      return "Credenziali inviate; risposta firmware non riconosciuta entro 15 secondi. Il flusso continua con esito sconosciuto.";
    if (stage === "stop_scan_error")
      return "iOS non ha chiuso correttamente la scansione BLE prima della connessione.";
    if (stage === "connection_check_warning")
      return "iOS non ha restituito lo stato precedente del dispositivo; il collegamento prosegue in modalità protetta.";
    if (stage === "connect_attempt_start")
      return "iOS sta negoziando connessione e servizi GATT. Il watchdog interromperà il tentativo se non risponde.";
    if (stage === "native_disconnected")
      return "Il materasso o iOS ha interrotto la connessione BLE dopo il collegamento.";
    if (stage === "connect_failed" || stage === "connect_attempt_error") {
      if (detail.includes("timeout"))
        return "Timeout CoreBluetooth: il dispositivo è visibile, ma non completa connessione o scoperta dei servizi GATT.";
      return "Connessione rifiutata da iOS o dal dispositivo. Il dettaglio completo è incluso nel report.";
    }
    return "Raccolta dati diagnostici in corso.";
  })();

  return (
    <div
      className="rounded-2xl border border-cyan-300/20 bg-[#101719] p-3 space-y-2"
      data-testid="ble-diagnostics-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Wrench size={13} className="text-cyan-300 shrink-0" />
          <div className="text-white/90 text-[12px] font-semibold">
            Diagnostica connessione
          </div>
        </div>
        <button
          type="button"
          onClick={copyReport}
          data-testid="ble-copy-diagnostics-button"
          className="h-8 px-2.5 rounded-lg border border-white/10 bg-white/5 text-white/75 text-[11px] flex items-center gap-1.5"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copiato" : "Copia report"}
        </button>
      </div>
      <div className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1 text-[10.5px] font-mono">
        <span className="text-white/40">Stato</span>
        <span className="text-cyan-200 break-all" data-testid="ble-diagnostic-state">
          {handshakeState}
        </span>
        <span className="text-white/40">Ultima fase</span>
        <span className="text-white/75 break-all" data-testid="ble-diagnostic-last-stage">
          {lastEvent?.stage || "avvio"}
        </span>
        <span className="text-white/40">Plugin BLE</span>
        <span className="text-white/75" data-testid="ble-diagnostic-plugin-version">
          {BLE_PLUGIN_VERSION} · Capacitor 7
        </span>
        <span className="text-white/40">Tentativo</span>
        <span className="text-white/75" data-testid="ble-diagnostic-attempt">
          {lastEvent?.attempt || "—"}
        </span>
        <span className="text-white/40">Ultimo HEX</span>
        <span
          className="text-emerald-200 break-all"
          data-testid="ble-diagnostic-last-hex"
        >
          {lastHexEvent?.hex || "—"}
        </span>
        {error && (
          <>
            <span className="text-white/40">Errore</span>
            <span className="text-rose-300 break-all" data-testid="ble-diagnostic-error">
              {error}
            </span>
          </>
        )}
      </div>
      <div
        className="rounded-lg border border-cyan-300/15 bg-cyan-300/[0.06] px-2.5 py-2 text-[11px] leading-snug text-cyan-50/80"
        data-testid="ble-diagnostic-interpretation"
      >
        {diagnosis}
      </div>
      <div className="max-h-36 overflow-y-auto border-t border-white/10 pt-2 space-y-0.5 text-[10px] font-mono text-white/60">
        {events.slice(-20).map((event, index) => (
          <div key={`${event.at}-${index}`} className="break-all">
            {formatEventTime(event.at)} · {event.stage}
            {event.byteLength != null ? ` · ${event.byteLength}B` : ""}
            {event.hex ? ` · ${event.hex}` : ""}
            {event.error ? ` · ${event.error}` : ""}
            {event.reconstructedBuffer
              ? ` · buffer=${event.reconstructedBuffer}`
              : ""}
          </div>
        ))}
      </div>
    </div>
  );
};